import { useMutation } from '@apollo/client'
import { Alert, ModalConfirm, useMessage } from '@faceup/ui-base'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'

const INVESTIGATION_BACKUP_KEYS_HELP_URL =
  'https://app.notion.com/p/Investigation-backup-keys-3755067b79cf80269e36e5b6658f1928'

const fragments = {
  TransferOwnershipModal_member: graphql(`
    fragment TransferOwnershipModal_member on Member {
      id
      name
    }
  `),
  TransferOwnershipModal_institution: graphql(`
    fragment TransferOwnershipModal_institution on Company {
      id
      isE2EE
      hasInvestigationBackupKeys
    }
  `),
}

const mutations = {
  TransferOwnership: graphql(`
    mutation TransferOwnershipMutation($memberId: UUID!, $motherId: UUID!) {
      transferOwnership(input: { memberId: $memberId, motherId: $motherId }) {
        oldOwner {
          id
          accountType(motherId: $motherId)
        }

        newOwner {
          id
          accountType(motherId: $motherId)
        }
      }
    }
  `),
}

type TransferOwnershipModalProps = {
  member: FragmentType<typeof fragments.TransferOwnershipModal_member> | null
  institution: FragmentType<typeof fragments.TransferOwnershipModal_institution>
  onClose: () => void
}

export const TransferOwnershipModal = ({
  member: _member,
  institution: _institution,
  onClose,
}: TransferOwnershipModalProps) => {
  const member = getFragmentData(fragments.TransferOwnershipModal_member, _member)
  const institution = getFragmentData(fragments.TransferOwnershipModal_institution, _institution)
  const message = useMessage()
  const [transfer, { loading }] = useMutation(mutations.TransferOwnership, {
    onCompleted: () => {
      message.success('Ownership transferred successfully')
      onClose()
    },
  })

  if (!member) {
    return null
  }

  return (
    <ModalConfirm
      title='Transfer ownership'
      open
      onCancel={onClose}
      onConfirm={async () => {
        transfer({
          variables: {
            memberId: member.id,
            motherId: institution.id,
          },
        })
      }}
      confirmState={{ loading }}
    >
      {institution.isE2EE && institution.hasInvestigationBackupKeys && (
        <Alert
          type='warning'
          message={
            <>
              End-to-end encryption is enabled — backup access to investigations cannot be migrated
              and will be lost after transfer.{' '}
              <a href={INVESTIGATION_BACKUP_KEYS_HELP_URL} target='_blank' rel='noreferrer'>
                What are investigation backup keys?
              </a>
            </>
          }
          style={{ marginBottom: 12 }}
        />
      )}
      Are you sure you want to transfer ownership to <strong>{member.name}</strong>?
    </ModalConfirm>
  )
}
